<script setup lang="ts">
import type { NavHook } from '~~/shared/types/navigation'

/**
 * Left column of the mega menu
 * Displays the "hook" - category label, headline, and value proposition
 * Following pattern for contextual sales messaging
 */

defineProps<{
  hook: NavHook
}>()
</script>

<template>
  <div class="flex flex-col">
    <!-- Category label (green) -->
    <span class="text-sm font-semibold text-primary-500">
      {{ hook.label }}
    </span>

    <!-- Headline (bold, uppercase) -->
    <h3 class="mt-2 text-lg font-extrabold uppercase tracking-tight text-slate-900">
      {{ hook.headline }}
    </h3>

    <!-- Value proposition -->
    <p class="mt-4 text-sm leading-relaxed text-neutral-600">
      {{ hook.description }}
    </p>

    <!-- Optional link (e.g., service areas) -->
    <NuxtLink
      v-if="hook.link"
      :to="hook.link.href"
      class="group mt-6 flex items-start gap-3"
    >
      <!-- Icon -->
      <Icon
        v-if="hook.link.icon"
        :name="hook.link.icon"
        size="20"
        class="mt-0.5 shrink-0 text-primary-500 transition-transform duration-200 group-hover:scale-110"
        aria-hidden="true"
      />

      <div class="min-w-0">
        <!-- Label -->
        <span class="block text-sm font-bold text-slate-800 transition-colors group-hover:text-primary-500">
          {{ hook.link.label }}
        </span>

        <!-- Description -->
        <span v-if="hook.link.description" class="mt-0.5 block text-sm leading-snug text-neutral-500">
          {{ hook.link.description }}
        </span>
      </div>
    </NuxtLink>

    <!-- Optional badge (e.g., license numbers, certifications) -->
    <p v-if="hook.badge" class="mt-6 text-xs font-medium text-neutral-500">
      {{ hook.badge }}
    </p>
  </div>
</template>
